/**
 * S65/S66/S68/S72 (historia 108) — what the alignment column of the fabrication order window puts
 * in one row.
 *
 * Pure on purpose: the states and the reasons are a table of Sun's, and a table is worth testing
 * without mounting a window around it.
 */
import { systemValueLabel } from '../../generic/systemValues/systemValueLabel'

/**
 * The three states Sun publishes in the listing (`SyncAlignment.State` minus `NEVER_SYNCED`, which
 * never travels: an order that was never synchronised arrives with BOTH fields absent).
 *
 * ⚠️ Anything outside this set shows NOTHING. A state this client does not know is not painted raw:
 * INV19 says an order with no alignment shows no invented value, and a code from a newer Sun is
 * exactly that -- a value this window cannot vouch for.
 */
const ALIGNMENT_STATES = ['ALIGNED', 'PENDING', 'MISALIGNED']

/**
 * Story 204 (RN1.5): ALIGNED and MISALIGNED are translated by their own code. Only PENDING keeps a key of its own
 * (P1-bis): `PENDING` is a shared key of this application (fabrication order status, raw material status filter,
 * incidences...) with another text, and historia 108 once overwrote its text in the 8 locales -- a regression outside
 * the story that no test saw, because they mock `t_`.
 */
const ALIGNMENT_STATE_KEYS = {
  PENDING: 'ALIGNMENT_PENDING'
}

const MISALIGNED = 'MISALIGNED'

/**
 * Los DOCE motivos de `SyncEventReason` que este cliente sabe traducir. Es el vocabulario de la
 * historia y vive aquí, en un solo sitio: lo usa el indicador de la ventana de OFs y, desde JUP-6,
 * la columna del motivo de las pantallas de cluster de Mantenimiento.
 *
 * ⚠️ Un código FUERA de esta lista viaja tal cual desde Sun -- no degrada a `OTHER`, para no tirar el
 * motivo real de un recurso futuro (`contrato-sun.md` §4). Por eso quien lo pinte tiene que
 * comprobar la pertenencia antes de traducir: un código desconocido NO se enseña en crudo.
 */
export const SYNC_REASON_CODES = [
  'APPLIED',
  'ALREADY_SENT',
  'STALE_DISCARDED',
  'DELETED_ALREADY_ABSENT',
  'IN_FLIGHT',
  'REPLICA_ABSENT',
  'DELETION_REFUSED_PRODUCTION',
  'RESYNC_DEGRADED',
  'QUANTITY_NOT_APPLICABLE',
  'DELIVERY_FAILED',
  'CODE_COLLISION',
  'OTHER'
]

/**
 * El subconjunto que puede acompañar a una OF MISALIGNED, que es lo único que la celda explica.
 *
 * Los que NO degradan (`APPLIED`, `ALREADY_SENT`, `STALE_DISCARDED`, `DELETED_ALREADY_ABSENT`,
 * `IN_FLIGHT`) están deliberadamente fuera: cuentan por qué una OF sigue BIEN, y esta celda solo
 * explica por qué una está mal.
 *
 * ⚠️ `OTHER` sí está: Sun lo documenta como "anything the story did not name" y lo hace DEGRADAR, y
 * `FabricationOrderRest:392` publica `reason.name()` sin filtrar, así que llega como cualquier otro.
 * El anuncio de Sun listaba once motivos y se lo dejó; sin su clave, S72 se rompería justo en el
 * caso comodín.
 */
const MISALIGNMENT_REASONS = [
  'REPLICA_ABSENT',
  'DELETION_REFUSED_PRODUCTION',
  'RESYNC_DEGRADED',
  'QUANTITY_NOT_APPLICABLE',
  'DELIVERY_FAILED',
  'CODE_COLLISION',
  'OTHER'
]

/**
 * What one row shows in the alignment column.
 *
 * Four readings and only four (S65): aligned, pending, not aligned with its reason, and -- for an
 * order that was never synchronised with the node -- nothing at all, with no default filled in
 * (INV19/S68). An unrecognised reason falls back to the bare state rather than to the server code:
 * saying less beats showing a person an identifier (S72).
 *
 * @param {Object} row Fabrication order as the listing delivers it
 * @param {Function} t_ Translation function of the window
 * @returns {string} Text of the cell, always a string React can paint
 */
export const alignmentCellText = (row, t_) => {
  const state = row?.alignment
  if (!ALIGNMENT_STATES.includes(state)) {
    return ''
  }
  const stateLabel = systemValueLabel(t_, 'SyncAlignment.State', state, ALIGNMENT_STATE_KEYS)
  if (state !== MISALIGNED) {
    return stateLabel
  }
  const reason = row?.alignmentReason
  if (!MISALIGNMENT_REASONS.includes(reason)) {
    return stateLabel
  }
  return stateLabel + ': ' + systemValueLabel(t_, 'SyncEventReason', reason)
}

/**
 * Whether this order has ever been synchronised with the node holding its replica.
 *
 * S69/S102: it is the last of the four conditions that gate the resync action. An order with no
 * indicator was never sent, so there is nothing to re-align -- and that is also why the cell window
 * needs no separate provenance check: an order born there was never replicated, so it never carries
 * an indicator either.
 *
 * @param {Object} row Fabrication order as the listing delivers it
 * @returns {boolean} Whether the order carries an alignment state
 */
export const hasAlignmentIndicator = (row) => ALIGNMENT_STATES.includes(row?.alignment)

export default alignmentCellText
